<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.updated {
    color: green;
}

.unit-details-container {
    .unit-details-card {
        display: flex;
        flex-direction: column;
        width: 100%;
        color: $theme-dark-grey;

        .command {
            display: flex;
            flex-direction: column;
            align-items: left;
            color: $theme-dark-grey;
            margin: 10px 5%;

            .title {
                font-size: 1.2em;
                font-weight: bold;
            }

            .command-button-ctn {
                width: 150px;
                border: 2px solid $theme-light-blue;
                border-radius: 3px;

                &:hover:not(:active) {
                    background-color: 2px solid
                        color.adjust($theme-light-blue, $lightness: -5%);
                }

                .command-button {
                    min-width: 148px;
                    padding: 5px 10px;
                    background-color: $theme-light-blue;
                    border-style: none;
                    color: white;

                    &:hover {
                        background-color: color.adjust(
                            $theme-light-blue,
                            $lightness: -5%
                        );
                    }

                    &:focus {
                        outline: none;
                    }

                    &.set,
                    &:active {
                        transform: scale(0.98, 0.96);
                    }
                }
            }
        }

        .update-time-row {
            margin: 0 0 12px 5px;

            .update-text {
                font-weight: bold;
                margin-right: 8px;
            }
        }

        .collapsible-block {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .fa,
            .fas {
                margin-right: 8px;
                font-size: 1.2em;

                &.fa-check {
                    color: $theme-green;
                }

                &.fa-exclamation-triangle {
                    color: $theme-orange;
                }

                &.fa-exclamation-circle {
                    color: $theme-red;
                }
            }
        }

        .button-row {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;

            .button {
                min-width: 160px;
            }
        }

        .status-content {
            padding: 5px 10px;

            div:last-child() {
                border-bottom: none;
            }

            .content-block {
                border-bottom: 1px dashed $theme-light-grey;
                color: $theme-grey;
                position: relative;

                .grey-out {
                    position: absolute;
                    left: -10px;
                    top: -5px;
                    height: calc(100% + 5px);
                    width: calc(100% + 20px);
                    background-color: color.adjust($theme-grey, $alpha: -0.8);
                    display: flex;
                    align-items: flex-start;
                    justify-content: flex-end;

                    label {
                        padding: 10px 15px;
                        border-bottom-left-radius: 5px;
                        background-color: color.adjust(
                            $theme-red,
                            $alpha: -0.4
                        );
                        color: white;
                        font-weight: 600;
                    }
                }

                .heading {
                    font-size: 0.9em;
                    font-weight: bold;
                    color: $theme-dark-grey;
                }

                .prop {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin: 3px 0 3px 18px;

                    .fa-exclamation {
                        color: $theme-red;
                        position: absolute;
                        left: -17px;
                        top: 3px;
                        display: none;
                        font-size: 0.9em;
                    }

                    &.problem {
                        color: $theme-red;

                        .fa-exclamation {
                            display: inline;
                        }
                    }

                    .text {
                        width: 47%;
                        font-size: 0.9em;
                        text-indent: -0.5em;

                        &.center {
                            text-align: center;
                        }
                    }

                    .space-between {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                }
            }
        }

        .collapsibles-col {
            display: flex;
            flex-flow: column;

            .collapsibles-block {
                flex-flow: column;
            }
        }

        .collapsible-container {
            margin-bottom: 10px;
        }

        @media (min-width: 850px) {
            .collapsibles-col {
                .collapsibles-block {
                    display: flex;
                    flex-flow: row nowrap;
                    justify-content: space-around;

                    .collapsible-container {
                        width: 47%;
                    }
                }
            }
        }
    }
}
</style>

<template>
    <div class="unit-details-card">
        <div class="update-time-row center-flex">
            <span class="update-text">{{ $t('lastLowBandwidthUpdate') }}:</span>
            <span :class="{ updated: hasHighlight }">{{
                lowBandwidthAge
            }}</span>
        </div>
        <div class="update-time-row center-flex">
            <span class="update-text"
                >{{ $t('lastHighBandwidthUpdate') }}:</span
            >
            {{ highBandwidthAge }}
        </div>
        <div class="button-row" v-if="!snapCollapsibles">
            <Button
                small
                @click="
                    buttonExpands
                        ? setAllCollapsibles(true)
                        : setAllCollapsibles(false)
                "
            >
                {{ buttonExpands ? $t('expandAll') : $t('collapseAll') }}
            </Button>
        </div>
        <div class="collapsibles-col">
            <div class="collapsibles-block">
                <Collapsible
                    :disabled="snapCollapsibles"
                    :isOpen="snapCollapsibles ? true : collapsiblesOpen.general"
                    @toggle="toggleCollapsible('general')"
                >
                    <template #button>
                        <div class="collapsible-block">
                            <div class="center-flex">
                                <i class="fa" :class="[generalStatusClass]"></i>
                                <span>{{ $t('generalStatus') }}</span>
                            </div>
                            <i
                                class="fa fa-caret-down toggle-caret"
                                v-if="!snapCollapsibles"
                            ></i>
                        </div>
                    </template>
                    <template #content>
                        <div class="status-content">
                            <div class="content-block">
                                <div
                                    class="grey-out"
                                    v-if="armState === 'ARM_STATE_FAILURE'"
                                >
                                    <label>{{ $t('mainBoardFailure') }}</label>
                                </div>
                                <span class="heading">{{
                                    $t('mainBoard')
                                }}</span>
                                <!-- <div class="prop" :class="{ problem: status.case_open }">
                                    <div class="text">{{ $t('caseOpen') }}</div>
                                    <div class="text">{{ status.case_open !== undefined ? status.case_open : $t('na') }}</div>
                                    <i class="fas fa-exclamation"/>
                                </div> -->
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('boardVersion') }}
                                    </div>
                                    <div class="text">
                                        {{
                                            mainBoardFullCheckin.hardware_version
                                        }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('firmwareVersion') }}
                                    </div>
                                    <div class="text">
                                        {{
                                            mainBoardFullCheckin.software_version
                                        }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">{{ $t('latitude') }}</div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        {{ roundValue(mainGps.latitude, 5) }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('longitude') }}
                                    </div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        {{ roundValue(mainGps.longitude, 5) }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('gpsTimestamp') }}
                                    </div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        {{ mainGpsTimestamp }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('positionOverruns') }}
                                    </div>
                                    <div class="text">
                                        {{
                                            mainBoardFullCheckin.position_log_overruns
                                        }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('eventOverruns') }}
                                    </div>
                                    <div class="text">
                                        {{
                                            mainBoardFullCheckin.event_log_overruns
                                        }}
                                    </div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('modemBoard')
                                }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('lastReportedState') }}
                                    </div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        {{ armState }}
                                    </div>
                                    <i class="fas fa-exclamation" />
                                </div>
                                <div class="prop">
                                    <div class="text">{{ $t('wifi') }}</div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        {{
                                            powerMode === 'POWER_MODE_SETUP'
                                                ? $t('enabled')
                                                : $t('disabled')
                                        }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('boardVersion') }}
                                    </div>
                                    <div class="text">
                                        {{ modemBoard.hardware_version }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('firmwareVersion') }}
                                    </div>
                                    <div class="text">
                                        {{ modemBoard.software_version }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('unitSerial') }}
                                    </div>
                                    <div class="text">
                                        {{ unit.unit_serial }}
                                    </div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{ $t('battery') }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('batteryVoltage') }}
                                    </div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        <!--highlightMap['battery_voltage'] }">-->
                                        {{ batteryVoltage }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('powerSource') }}
                                    </div>
                                    <div
                                        class="text"
                                        :class="{ updated: hasHighlight }"
                                    >
                                        <!--highlightMap['external_connected'] }">-->
                                        {{ powerSource }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Collapsible>
                <Collapsible
                    :disabled="snapCollapsibles"
                    :isOpen="snapCollapsibles ? true : collapsiblesOpen.cell"
                    @toggle="toggleCollapsible('cell')"
                >
                    <template #button>
                        <div class="collapsible-block">
                            <div class="center-flex">
                                <i class="fa" :class="[cellCommClass]"></i>
                                <span>{{ $t('communication_plural') }}</span>
                            </div>
                            <i
                                class="fa fa-caret-down toggle-caret"
                                v-if="!snapCollapsibles"
                            ></i>
                        </div>
                    </template>
                    <template #content>
                        <div class="status-content">
                            <div class="content-block">
                                <span class="heading">{{ $t('cell') }}</span>
                                <!-- <div class="prop">
                                    <div class="text">{{ $t('cellNumber') }}</div>
                                    <div class="text">{{ modemBoard.sim_number }}</div>
                                </div> -->
                                <div class="prop">
                                    <div class="text">IMEI</div>
                                    <div class="text">{{ cell.imei }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('rssiSignalPercent') }}
                                    </div>
                                    <div class="text">{{ cell.rssi }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('rssiUpdateCount') }}
                                    </div>
                                    <div class="text">
                                        {{ cell.rssi_update_count }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">ICCID</div>
                                    <div class="text">{{ cell.iccid }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">IP</div>
                                    <div class="text">{{ cell.ip }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('cellularCarrier') }}
                                    </div>
                                    <div class="text">{{ cell.carrier }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('modemConnected') }}
                                    </div>
                                    <div class="text">
                                        {{ cell.modem_connected }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">{{ $t('status') }}</div>
                                    <div class="text">{{ cell.status }}</div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">Iridium</span>
                                <div class="prop">
                                    <div class="text">IMEI</div>
                                    <div class="text">{{ iridium.imei }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">RSSI</div>
                                    <div class="text">{{ iridium.rssi }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('rssiUpdateCount') }}
                                    </div>
                                    <div class="text">
                                        {{ iridium.rssi_update_count }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('modemConnected') }}
                                    </div>
                                    <div class="text">
                                        {{ iridium.modem_connected }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">{{ $t('status') }}</div>
                                    <div class="text">{{ iridium.status }}</div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('wifiClient')
                                }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('connected') }}
                                    </div>
                                    <div class="text">
                                        {{ modemBoard.wifi_client_connected }}
                                    </div>
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('connectedSsid') }}
                                    </div>
                                    <div class="text">
                                        {{
                                            modemBoard.wifi_client_connected_ssid
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Collapsible>
            </div>
            <div class="collapsibles-block">
                <Collapsible
                    :disabled="snapCollapsibles"
                    :isOpen="
                        snapCollapsibles ? true : collapsiblesOpen.manifest
                    "
                    @toggle="toggleCollapsible('manifest')"
                >
                    <template #button>
                        <div class="collapsible-block">
                            <div class="center-flex">
                                <i class="fa fa-check"></i>
                                <span>{{ $t('sensor_plural') }}</span>
                            </div>
                            <i
                                class="fa fa-caret-down toggle-caret"
                                v-if="!snapCollapsibles"
                            ></i>
                        </div>
                    </template>
                    <template #content>
                        <div class="status-content">
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('manifest')
                                }}</span>
                                <div
                                    v-if="
                                        Object.keys(manifestSensorItems)
                                            .length > 0
                                    "
                                >
                                    <div
                                        class="prop"
                                        v-for="(
                                            count, type, index
                                        ) in manifestSensorItems"
                                        :key="index"
                                    >
                                        <div class="text">{{ type }}</div>
                                        <div class="text">{{ count }}</div>
                                    </div>
                                </div>
                                <div v-else class="prop">
                                    <div class="text">
                                        {{ $t('noManifestSensors') }}
                                    </div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('unmonitored')
                                }}</span>
                                <div
                                    v-if="
                                        Object.keys(unmonitoredSensorItems)
                                            .length > 0
                                    "
                                >
                                    <div
                                        class="prop"
                                        v-for="(
                                            count, type, index
                                        ) in unmonitoredSensorItems"
                                        :key="index"
                                    >
                                        <div class="text">{{ type }}</div>
                                        <div class="text">{{ count }}</div>
                                    </div>
                                </div>
                                <div v-else class="prop">
                                    <div class="text">
                                        {{ $t('noUnmonitoredSensors') }}
                                    </div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">ZWave</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('zwaveMode') }}
                                    </div>
                                    <div class="text">{{ zwaveMode }}</div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Collapsible>
                <Collapsible
                    :disabled="snapCollapsibles"
                    :isOpen="
                        snapCollapsibles ? true : collapsiblesOpen.provisioning
                    "
                    @toggle="toggleCollapsible('provisioning')"
                >
                    <template #button>
                        <div class="collapsible-block">
                            <div class="center-flex">
                                <i class="fa" :class="[provisioningClass]"></i>
                                <span>{{ $t('provisioning') }}</span>
                            </div>
                            <i
                                class="fa fa-caret-down toggle-caret"
                                v-if="!snapCollapsibles"
                            ></i>
                        </div>
                    </template>
                    <template #content>
                        <div class="status-content">
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('provisioningInfo')
                                }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('generatedTimestamp') }}
                                    </div>
                                    <div class="text">
                                        {{ provGeneratedTimestamp }}
                                    </div>
                                    <i class="fas fa-exclamation" />
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('claimedTimestamp') }}
                                    </div>
                                    <div class="text">
                                        {{ provClaimedTimestamp }}
                                    </div>
                                    <i class="fas fa-exclamation" />
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('identifier') }}
                                    </div>
                                    <div class="text">{{ provId }}</div>
                                    <i class="fas fa-exclamation" />
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('counter_plural')
                                }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('unitCounter') }}
                                    </div>
                                    <div class="text">{{ unitCounter }}</div>
                                    <i class="fas fa-exclamation" />
                                </div>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('serverCounter') }}
                                    </div>
                                    <div class="text">{{ serverCounter }}</div>
                                    <i class="fas fa-exclamation" />
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{ $t('wifi') }}</span>
                                <div class="prop">
                                    <div class="text">
                                        {{ $t('networkName') }}
                                    </div>
                                    <div class="text">{{ wifiSsid }}</div>
                                </div>
                                <div class="prop">
                                    <div class="text">{{ $t('password') }}</div>
                                    <div class="text space-between">
                                        {{
                                            PasswordVisible
                                                ? wifiPassword
                                                : maskedPassword
                                        }}
                                        <div
                                            class="visible-button"
                                            :class="{
                                                visible: PasswordVisible,
                                            }"
                                            @click="
                                                PasswordVisible =
                                                    !PasswordVisible
                                            "
                                        >
                                            <i
                                                class="fa"
                                                :class="{
                                                    'fa-eye': PasswordVisible,
                                                    'fa-eye-slash':
                                                        !PasswordVisible,
                                                }"
                                            ></i>
                                        </div>
                                    </div>
                                </div>
                                <div class="command">
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="getCertificates"
                                        >
                                            {{ $t('certificates') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="content-block">
                                <span class="heading">{{
                                    $t('dataServicesPlatform')
                                }}</span>
                                <div class="command">
                                    <div class="command-button-ctn">
                                        <button
                                            class="command-button"
                                            @click="gotoUnitDSP"
                                        >
                                            {{ $t('goToUnitInDSP') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Collapsible>
            </div>
        </div>
    </div>
</template>

<script>
import { getAgeString } from '../../../util/util.js';
import { mapGetters, mapState } from 'vuex';

export default {
    name: 'UnitLastData',

    props: {
        unit: Object,
        snapCollapsibles: Boolean,
    },

    data() {
        return {
            collapsiblesOpen: {
                provisioning: false,
                cell: false,
                manifest: false,
                general: false,
            },
            buttonExpands: true,
            ageLoop: undefined,
            unitAge: undefined,
            lowBandwidthAge: undefined,
            highBandwidthAge: undefined,
            hasHighlight: false,
            PasswordVisible: false,
        };
    },

    computed: {
        ...mapGetters(['timeFormatter']),
        ...mapState(['authToken']),

        unitCounter() {
            return this.unit?.provisioning?.unit_counter ?? null;
        },

        serverCounter() {
            return this.unit?.provisioning?.server_counter ?? null;
        },

        mainBoardFullCheckin() {
            return (
                this.unit?.last_full_checkin?.message?.high?.main_board_data ??
                {}
            );
        },

        mainBoardCheckin() {
            return this.unit?.last_checkin?.message?.low ?? null;
        },

        powerSource() {
            if (this.mainBoardCheckin) {
                if (this.mainBoardCheckin.external_connected) {
                    return this.$t('external');
                } else {
                    return this.$t('battery');
                }
            }
            return this.$t('na');
        },

        mainBoardConfig() {
            return (
                this.unit?.last_full_checkin?.message?.high
                    ?.main_board_config ?? {}
            );
        },

        mainGps() {
            return this.mainBoardCheckin?.gps ?? {};
        },

        mainGpsTimestamp() {
            if (this.mainBoardCheckin) {
                return this.$store.getters.timeFormatter.format(
                    new Date(this.mainBoardCheckin.gps.timestamp * 1000),
                );
            }
            return null;
        },

        modemBoard() {
            return (
                this.unit?.last_full_checkin?.message?.high?.modem_board_data ??
                {}
            );
        },

        modemGps() {
            return this.modemBoard?.gps ?? {};
        },

        modemGpsTimestamp() {
            if (this.modemGps && this.modemGps.timestamp) {
                return this.$store.getters.timeFormatter.format(
                    new Date(this.modemGps.timestamp * 1000),
                );
            }
            return null;
        },

        cell() {
            return this.modemBoard?.cell ?? {};
        },

        iridium() {
            return this.modemBoard?.iridium ?? {};
        },

        info() {
            return this.unit?.last_full_checkin?.message?.high?.unit ?? {};
        },

        armState() {
            return this.unit?.last_checkin?.message?.low?.arm_state ?? {};
        },

        batteryVoltage() {
            const mv = this.unit?.last_checkin?.message?.low?.battery_voltage;
            return mv == null ? this.$t('na') : `${mv / 1000.0}V`;
        },

        status() {
            return this.unit?.last_data?.head_status ?? {};
        },

        sensors() {
            return this.unit?.sensors ?? [];
        },

        gps() {
            return this.unit?.last_checkin?.message?.low?.gps ?? {};
        },

        gps() {
            return this.unit?.last_checkin?.message?.low?.gps ?? {};
        },

        gpsTimestamp() {
            const ticks = this.gps.timestamp;
            return ticks;
        },

        timestamp() {
            if (this.gps.timestamp) {
                return new Date(parseInt(this.gps.timestamp)).toLocaleString();
            } else {
                return this.$t('na');
            }
        },

        provClaimedTimestamp() {
            if (
                this.unit.provisioning &&
                this.unit.provisioning.claimed_timestamp
            ) {
                return this.timeFormatter.format(
                    new Date(this.unit.provisioning.claimed_timestamp),
                );
            } else {
                return undefined;
            }
        },

        provGeneratedTimestamp() {
            if (
                this.unit.provisioning &&
                this.unit.provisioning.generated_timestamp
            ) {
                return this.timeFormatter.format(
                    new Date(this.unit.provisioning.generated_timestamp),
                );
            } else {
                return undefined;
            }
        },

        provId() {
            return this.unit?.provisioning?.id ?? -1;
        },

        sensors() {
            return this.mainBoardFullCheckin?.zwave_sensors ?? [];
        },

        configs() {
            if (
                this.mainBoardConfig &&
                this.mainBoardConfig.zwave_sensor_configs
            ) {
                const rawConfigs = this.mainBoardConfig.zwave_sensor_configs;
                const configs = {};
                for (const config of rawConfigs) {
                    configs[config.node_id] = config;
                }
                return configs;
            }
            return {};
        },

        manifestSensors() {
            if (this.sensors) {
                return this.sensors.filter(
                    (s) =>
                        this.configs[s.node_id] &&
                        this.configs[s.node_id].monitor_settings.monitor,
                );
            }
            return [];
        },

        unmonitoredSensors() {
            if (this.sensors) {
                return this.sensors.filter(
                    (s) =>
                        !(
                            this.configs[s.node_id] &&
                            this.configs[s.node_id].monitor_settings.monitor
                        ),
                );
            }
            return [];
        },

        manifestSensorItems() {
            const counts = {};

            this.manifestSensors.forEach((s) => {
                const model = s.sensor_type
                    ? s.sensor_type.split('_')[1]
                    : this.$t('unknown');
                if (counts[model]) {
                    counts[model]++;
                } else {
                    counts[model] = 1;
                }
            });

            return counts;
        },

        unmonitoredSensorItems() {
            const counts = {};

            this.unmonitoredSensors.forEach((s) => {
                const model = s.sensor_type.split('_')[1];
                if (counts[model]) {
                    counts[model]++;
                } else {
                    counts[model] = 1;
                }
            });

            return counts;
        },

        zwaveMode() {
            return (
                this.unit?.last_full_checkin?.message?.high?.main_board_config
                    ?.zwave_mode ?? ''
            );
        },

        cellCommClass() {
            let classStr = 'fa-check';

            // if (this.comms.iridium_comm_active && !this.comms.cell_comm_active) {
            //     classStr = 'fa-exclamation-triangle';
            // }

            // if ((!this.comms.cell_comm_active && !this.comms.iridium_comm_active) ||
            //     this.comms.cell_simcard_error ||
            //     this.comms.cell_account_error ||
            //     this.comms.cell_not_subscribed) {
            //         classStr = 'fa-exclamation-circle';
            // }

            return classStr;
        },

        otherCommClass() {
            let classStr = 'fa-check';

            // if (this.comms.cell_comm_active && !this.comms.iridium_comm_active) {
            //     classStr = 'fa-exclamation-triangle';
            // }

            // if ((!this.comms.cell_comm_active && !this.comms.iridium_comm_active) ||
            //     this.comms.iridium_send_error ||
            //     !this.comms.gps_comm_active ||
            //     !this.comms.moteino_comm_active) {
            //         classStr = 'fa-exclamation-circle';
            // }

            return classStr;
        },

        provisioningClass() {
            return 'fa-check';
        },

        generalStatusClass() {
            let classStr = 'fa-check';

            if (this.armState === 'ARM_STATE_FAILURE') {
                classStr = 'fa-exclamation-circle';
            }

            return classStr;
        },

        lastUpdateTime() {
            if (this.unit && this.unit.timestamp) {
                return new Date(this.unit.timestamp).valueOf();
            } else {
                return 0;
            }
        },

        formattedAge() {
            if (this.lastUpdateTime) {
                return getAgeString(
                    new Date().valueOf() - this.lastUpdateTime,
                    true,
                );
            } else {
                return this.$t('na');
            }
        },

        powerMode() {
            return (
                this.unit?.last_checkin?.message?.low?.power_mode ??
                this.unit?.power_mode ??
                ''
            );
        },

        wifiSsid() {
            return this.unit?.wifi_ssid ?? '';
        },

        wifiPassword() {
            return this.unit?.wifi_password ?? '';
        },

        maskedPassword() {
            return '*'.repeat(this.wifiPassword?.length || 0);
        },
    },

    watch: {
        // collapsiblesOpen: {
        //     deep: true,
        //     immediate: true,
        //     handler: function(newVal) {
        //         console.log('new val: ', newVal);
        //     }
        // },
        'unit.last_checkin': {
            handler(newVal, oldVal) {
                const newLow = newVal?.message?.low;
                const oldLow = oldVal?.message?.low;

                // Only highlight on non-full checkin
                if (!newLow || !oldLow || newVal?.message?.high !== undefined) {
                    this.hasHighlight = false;
                    return;
                }

                this.hasHighlight = true;
            },
            immediate: false,
        },
    },

    created() {
        this.updateAge();
        this.ageLoop = setTimeout(this.updateAge, 1000);

        // set general status has highlight flag on load/refresh
        if (this.unit.last_checkin.message.high === undefined) {
            this.hasHighlight = true;
        }
    },

    beforeDestroy() {
        if (this.ageLoop) {
            clearTimeout(this.ageLoop);
            this.ageLoop = undefined;
        }
    },

    methods: {
        roundValue(value, decimals) {
            if (!value) return 0;
            return Number(value.toFixed(decimals));
        },

        setAllCollapsibles(shouldOpen = false) {
            Object.keys(this.collapsiblesOpen).forEach((key) => {
                this.collapsiblesOpen[key] = shouldOpen;
            });

            if (shouldOpen) {
                this.buttonExpands = false;
            } else {
                this.buttonExpands = true;
            }

            this.checkCollapsiblesOpen();
        },

        toggleCollapsible(collapsible) {
            this.collapsiblesOpen[collapsible] =
                !this.collapsiblesOpen[collapsible];

            this.checkCollapsiblesOpen();
        },

        checkCollapsiblesOpen() {
            let numberOpen = 0;
            let numberClosed = 0;

            Object.keys(this.collapsiblesOpen).forEach((key) => {
                if (this.collapsiblesOpen[key]) {
                    numberOpen++;
                } else {
                    numberClosed++;
                }
            });

            // If none are open, then the button must be for expand all, and the inverse
            if (!numberOpen) {
                this.buttonExpands = true;
            } else if (!numberClosed) {
                this.buttonExpands = false;
            }
        },

        latLongError() {
            if (this.gps && this.gps.latitude && this.gps.longitude) {
                const lat = Number(this.gps.latitude);
                const long = Number(this.gps.longitude);
                return (lat === 0 && long === 0) || (lat === -1 && long === -1);
            } else {
                return true;
            }
        },

        // Designed to update the age view of the unit and all sensors
        updateAge() {
            if (this.unit.last_checkin) {
                this.lowBandwidthAge = getAgeString(
                    new Date().valueOf() -
                        new Date(this.unit.last_checkin.timestamp).valueOf(),
                    true,
                    true,
                );
                this.highBandwidthAge = getAgeString(
                    new Date().valueOf() -
                        new Date(
                            this.unit.last_full_checkin.timestamp,
                        ).valueOf(),
                    true,
                    true,
                );

                // Update sensor ages
            }

            // Reset this loop
            if (this.ageLoop) {
                this.ageLoop = setTimeout(this.updateAge, 1000);
            }
        },

        gotoUnitDSP() {
            window.open(
                'https://dataserviceplatform.com/PortalV2/Admin/#device/detail?serialNumber=' +
                    this.unit.unit_serial +
                    '&deviceType=153510-000231',
                '_blank',
                'noreferrer',
            );
        },

        async getCertificates() {
            await this.downloadFile('AppareoGatewayCA.crt');
            await this.downloadFile('gw_mobile_client.pfx');
        },

        async downloadFile(filename) {
            return new Promise(async (resolve, reject) => {
                try {
                    console.log(filename);
                    let response = await fetch(`/api/wificerts/${filename}`, {
                        headers: {
                            Authorization: `Bearer ${this.authToken}`,
                        },
                    });

                    if (response.status === 200) {
                        let blob = await response.blob();
                        console.log(blob.size);
                        let url = window.URL.createObjectURL(blob);
                        let docElement = document.createElement('a');
                        docElement.href = url;
                        docElement.download = filename;
                        document.body.appendChild(docElement);
                        docElement.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(docElement);
                        resolve();
                    } else {
                        console.error('Failed to download file');
                        reject('Failed to download file');
                    }
                } catch (error) {
                    console.error('Error downloading file:', error);
                    reject('Error downloading file');
                }
            });
        },
    },
};
</script>
